import React, { useEffect, useCallback, useMemo } from 'react';
import { Button, TextField } from '@cimpress/react-components';
import { Truck } from '../../../models/truck';
import { Dictionary } from '../../../models/utils';
import { ShipmentGroup } from '../../../models/shipment-group';
import { valueToArray } from '../../../utils/functions';
import { Select } from '../../Select';
import { FlexBox, JustifyContent } from '../../../utils/css-classes';
import Link from '../../../common/Link';
import { isShipmentGroupAllowed } from '../../../utils/trucks/is-shipment-group-allowed';
import { isOptionDisabled, sortOptions } from '../../../utils/options';
import { useShipmentGroups } from '../../../store/hooks/useShipmentGroups';
import { useTrucks } from '../../../store/hooks/useTrucks';

export interface LoadShipmentGroupFormProps {
  truck: Truck;
  hide: () => void;
}

const tagMap: Record<string, string> = {
  'Declared - US FDA': 'declared_fda',
  'Declared - General': 'declared_normal',
  Undeclared: 'undeclared',
};

// tslint:disable-next-line:function-name
export default function LoadShipmentGroupForm({
  truck,
  hide,
}: LoadShipmentGroupFormProps) {
  const {
    shipmentGroups,
    getShipmentGroups,
    isShipmentGroupsLoading,
  } = useShipmentGroups();
  const { loadShipmentGroup } = useTrucks();
  const [palletBoxCounts, setPalletBoxCounts] = React.useState<Dictionary<number>>({});
  const [
    shipmentGroup,
    setShipmentGroup,
  ] = React.useState<ShipmentGroup | null>(shipmentGroups[0] || null);
  const [sortedShipmentGroups, setSortedShipmentGroups] = React.useState<ShipmentGroup[]>([]);
  const [showAll, setShowAll] = React.useState(false);

  const isAllowedInTruck = useCallback(
    isShipmentGroupAllowed(truck, tagMap),
    [truck],
  );

  const makeShipmentGroupOption = useCallback(
    (obj: ShipmentGroup) => ({
      label: `${obj.name} (${obj.shipmentCount})`,
      value: obj.id,
      isDisabled: !isAllowedInTruck(obj) || obj.manualLoadDisabled,
    }),
    [isAllowedInTruck],
  );

  const shipmentGroupOptions = useMemo(
    () => sortedShipmentGroups
      .map(makeShipmentGroupOption)
      .filter(option => !isOptionDisabled(option) || showAll)
      .sort(sortOptions),
    [makeShipmentGroupOption, sortedShipmentGroups, showAll],
  );

  useEffect(
    () => {
      getShipmentGroups();
      setShipmentGroup(sortedShipmentGroups[0] || null);
    },
    [truck],
  );

  useEffect(
    () => {
      if (shipmentGroups) {
        setSortedShipmentGroups(
          Object.values(shipmentGroups)
            .filter(shipmentGroup => shipmentGroup.shipmentCount)
            .sort(
              (firstValue, secondValue) =>
                firstValue!.name.localeCompare(secondValue!.name),
            ),
        );
      }
    },
    [shipmentGroups],
  );

  useEffect(
    () => {
      if (
        !shipmentGroup ||
        !shipmentGroups[shipmentGroup.id] ||
        !shipmentGroups[shipmentGroup.id].shipmentCount ||
        !isAllowedInTruck(shipmentGroup)
      ) {
        setShipmentGroup(
          sortedShipmentGroups
            .filter(isAllowedInTruck)
            .filter(group => !group.manualLoadDisabled)[0] || null,
        );
      }
    },
    [sortedShipmentGroups],
  );

  const setPalletBoxCount = (carrierAccountKey: string, count: number) => {
    setPalletBoxCounts(state => ({
      ...state,
      [carrierAccountKey]: count,
    }));
  };

  return (
    <div className={`m-2 ${FlexBox.FLEX} ${JustifyContent.CENTER}`}>
      <fieldset>
        <Select
          isLoading={isShipmentGroupsLoading}
          label="Select a group of shipments"
          value={shipmentGroup ? makeShipmentGroupOption(shipmentGroup) : undefined}
          options={shipmentGroupOptions}
          searchable={true}
          clearable={false}
          tether={true}
          isOptionDisabled={option => isOptionDisabled(option)}
          onChange={option => {
            const options = valueToArray(option);
            if (options.length) {
              const shipmentGroup = shipmentGroups[options[0].value];
              setShipmentGroup(shipmentGroup);
              setPalletBoxCounts({});
            } else {
              setShipmentGroup(null);
              setPalletBoxCounts({});
            }
          }}
        />
        <p style={{ fontSize: '0.8em', fontStyle: 'italic' }}>
          Shipment groups that do not match the truck's loading restrictions or are disabled are currently&nbsp;
          <b>{showAll ? 'shown' : 'hidden'}</b>.
          Click <Link onClick={() => setShowAll(!showAll)} text="here"/> to {showAll ? 'hide' : 'show'} them.
        </p>
        {shipmentGroup && shipmentGroup.internalShipmentGroups.map(
          ({ key, name, shipmentCount }) =>
            <div key={key}>
              <h6>{name} ({shipmentCount} shipments)</h6>
              <TextField
                type="number"
                label={'Number of pallet boxes'}
                onChange={e => setPalletBoxCount(key, parseInt(e.target.value, 10))}
                value={palletBoxCounts[key] || 1}
                min={1}
              />
            </div>,
        )}
        <div className={`pt-4 ${FlexBox.FLEX} ${JustifyContent.SPACE_BETWEEN}`}>
          <input type="submit" hidden />
          <Button
            className="mr-4 min-width-180"
            type="default"
            onClick={hide}
          >
            Cancel
            </Button>
          <Button
            disabled={
              !shipmentGroup ||
              !palletBoxCounts ||
              !isAllowedInTruck(shipmentGroup) ||
              shipmentGroup.manualLoadDisabled
            }
            className="min-width-180"
            type="primary"
            onClick={onSubmit}
          >
            Load Shipments
            </Button>
        </div>
      </fieldset>
    </div>
  );

  function onSubmit() {
    if (shipmentGroup) {
      loadShipmentGroup({
        truck,
        shipmentGroup,
        palletBoxCounts: shipmentGroup.internalShipmentGroups.map(
          ({ key }) => palletBoxCounts[key] || 1,
        ),
      });
    }
    hide();
  }
}
